import React, { useEffect, useRef, useState } from 'react'
import {
  CustomSteps,
  CustomStepsRef,
  ETypeOfNotification,
  InputDate,
  InputSwitch,
  InputTextCustom,
  TextArea,
  setNotification,
} from 'medere-ui-framework'
import { useDispatch } from 'react-redux'
import { classNames } from 'primereact/utils'
import { HealthEntitySaveDTO } from '../../../dto/HealthEntitySaveDTO'
import ResetAfipConnectionModal from '../../Profile/Views/tabs/modal/ResetAfipConnectionModal'
import { generateHealthEntityAfipPrivateKey, generateHealthEntityCSRCertificate, getHealthEntityCurrentAfipStep, saveHealthEntityAfipCertificate, testHealthEntityAfipConnection } from '../../../servicesHooks/HealthEntity/useHealthEntityService'


interface HealthEntityAfipBillingTabProps {
  healthEntity: HealthEntitySaveDTO
  setHealthEntity: React.Dispatch<React.SetStateAction<HealthEntitySaveDTO>>
  healthEntityId: number | null | undefined
  readOnly?: boolean
}

export default function HealthEntityAfipBillingTab({
  healthEntity,
  setHealthEntity,
  healthEntityId,
  readOnly = false,
}: Readonly<HealthEntityAfipBillingTabProps>) {
  const dispatch = useDispatch()
  const stepsRef = useRef<CustomStepsRef>(null)

  const [provisoryAfipAlias, setProvisoryAfipAlias] = useState(healthEntity.afipalias)
  const [trackedFiscalId, setTrackedFiscalId] = useState(healthEntity.cuit)
  const [openResetAfipConnectionModal, setOpenResetAfipConnectionModal] = useState(false)
  const [isAfipConnectionTestingLoading, setIsAfipConnectionTestingLoading] = useState(false)
  const [isAfipConnectionSuccessful, setIsAfipConnectionSuccessful] = useState<boolean | null>(null)
  const [dfCertificateDue, setDfCertificateDue] = useState<Date | null>(null)
  const [afipCertificate, setAfipCertificate] = useState('')

  useEffect(() => {
    if (!healthEntityId || healthEntityId <= 0) {
      stepsRef.current?.setActiveStep(0)
      return
    }
    getHealthEntityCurrentAfipStep(healthEntityId).then((response) => {
      if (typeof response === 'number') {
        stepsRef.current?.setActiveStep(response)
      }
    })
  }, [healthEntityId])

  useEffect(() => {
    if (trackedFiscalId === healthEntity.cuit) return
    setTrackedFiscalId(healthEntity.cuit)
    if ((stepsRef.current?.getActiveStep() ?? 0) > 0) {
      stepsRef.current?.setActiveStep(0)
      setIsAfipConnectionSuccessful(null)
    }
  }, [healthEntity.cuit, trackedFiscalId])

  function notifyMissingData(title: string) {
    dispatch(setNotification({
      title,
      typeOfNotification: ETypeOfNotification.WARNING,
    }))
  }

  function validateAfipBaseData() {
    if (!healthEntity.cuit?.trim()) {
      notifyMissingData('Completá el CUIT en la pestaña Facturación')
      return false
    }
    if (!healthEntity.afipalias?.trim()) {
      notifyMissingData('Completá el Alias en esta pestaña')
      return false
    }
    return true
  }

  return (
    <React.Fragment>
      <div className='health-entity-afip-tab'>
        <div className='config-container'>
          <fieldset className='afipSection'>
            <h3 className='container-title'>ARCA</h3>
            <div className='flex flex-col md:flex-row items-center health-entity-afip-header-row'>
              <InputDate
                label='Inicio de actividades'
                className='w-full md:w-3'
                calendarClassName='border2Date'
                style={{ marginRight: '10px' }}
                placeholder='mm/aaaa'
                view='month'
                dateFormat='mm/yy'
                maxDate={new Date()}
                value={healthEntity.companystartdate ? new Date(healthEntity.companystartdate) : null}
                onChange={(e) => setHealthEntity(prev => ({ ...prev, companystartdate: e.target.value }))}
                disable={readOnly}
              />
              <InputTextCustom
                value={healthEntity.afipalias}
                onChange={(e) => {
                  const nextAlias = e.target.value
                  setProvisoryAfipAlias(nextAlias)
                  if ((stepsRef.current?.getActiveStep() ?? 0) === 0) {
                    setHealthEntity(prev => ({ ...prev, afipalias: nextAlias }))
                    return
                  }
                  setOpenResetAfipConnectionModal(true)
                }}
                label='Alias'
                spanClassName='border2'
                className='md:w-2 white-component'
                disable={readOnly}
              />
              <div style={{ width: '50%', marginTop: '30px' }}>
                <InputSwitch
                  style={{ width: '100%' }}
                  label='Producción'
                  className='ml-2 md:w-3'
                  checked={!!healthEntity.afipproductionmode}
                  onChange={(e) => setHealthEntity(prev => ({ ...prev, afipproductionmode: !!e.value }))}
                  disabled={readOnly}
                />
              </div>
            </div>
            <div className='flex flex-column p-2 md:p-4 lg:p-6'>
              <CustomSteps
                ref={stepsRef}
                manualyStepSetting={true}
                steps={(activeStep) => [
                  {
                    title: 'Paso 1 - Clave privada',
                    icon: 'pi-key',
                    mainBtn: {
                      label: 'Generar',
                      className: 'btn-ghost',
                      onClick: () => {
                        if (readOnly) return
                        if (!validateAfipBaseData() || !healthEntityId || healthEntityId <= 0) return
                        return generateHealthEntityAfipPrivateKey(
                          healthEntityId,
                          healthEntity.afipalias,
                          healthEntity.cuit,
                          healthEntity.afipproductionmode ?? false,
                          healthEntity.iibbperceptionagent ?? false,
                        ).then((response) => {
                          if (!response) {
                            dispatch(setNotification({
                              title: 'Hemos tenido un error para generar tu clave privada',
                              icon: 'pi-times',
                              typeOfNotification: ETypeOfNotification.ERROR,
                            }))
                            return
                          }
                          stepsRef.current?.nextStep()
                        })
                      },
                    },
                    description: <p className='text-bluegray-500'>Vamos a generar una clave privada para conectarte con ARCA</p>,
                    buttonResult: activeStep === 0 ? '' : <span className='primary ml-3 mr-2'>Clave generada <i className='pi pi-check border-circle shadow-1 p-1' /></span>,
                  },
                  {
                    title: 'Paso 2 - Archivo CSR',
                    icon: 'pi-paperclip',
                    mainBtn: {
                      label: 'Generar',
                      className: 'btn-ghost',
                      onClick: () => {
                        if (readOnly || !healthEntityId || healthEntityId <= 0) return
                        return generateHealthEntityCSRCertificate(healthEntityId).then((response) => {
                          if (!response) {
                            dispatch(setNotification({
                              title: 'Hemos tenido un error para generar el archivo CSR',
                              icon: 'pi-times',
                              typeOfNotification: ETypeOfNotification.ERROR,
                            }))
                            return
                          }
                          stepsRef.current?.nextStep()
                          navigator.clipboard.writeText(response).then(() => {
                            dispatch(setNotification({
                              title: 'Tu CSR fue copiado al portapapeles',
                              icon: 'pi-check',
                              typeOfNotification: ETypeOfNotification.INFO,
                            }))
                          })
                        })
                      },
                    },
                    description: <p className='text-bluegray-500'>Vamos a generar y copiar el contenido del archivo CSR para cargarlo luego en ARCA.</p>,
                  },
                  {
                    title: 'Paso 3 - Pegar texto de Certificado de ARCA',
                    icon: 'pi-save',
                    mainBtn: {
                      label: 'Guardar',
                      className: 'btn-ghost',
                      onClick: () => {
                        if (readOnly || !healthEntityId || healthEntityId <= 0) return
                        if (!dfCertificateDue) {
                          notifyMissingData('Completá el vencimiento del certificado')
                          return
                        }
                        if (!afipCertificate) {
                          notifyMissingData('Ingresá el certificado de ARCA en el área de texto')
                          return
                        }
                        return saveHealthEntityAfipCertificate(healthEntityId, dfCertificateDue, afipCertificate).then((response) => {
                          if (!response) {
                            dispatch(setNotification({
                              title: 'Hemos tenido un error para guardar tu certificado de ARCA',
                              icon: 'pi-times',
                              typeOfNotification: ETypeOfNotification.ERROR,
                            }))
                            return
                          }
                          stepsRef.current?.nextStep()
                        })
                      },
                    },
                    description: (
                      <div className='flex flex-col health-entity-afip-certificate-block'>
                        <p className='text-bluegray-500 mb-4'>Pegá acá el texto del certificado provisto por ARCA.</p>
                        <InputDate
                          className='w-full md:w-6'
                          calendarClassName='border2Date'
                          label='Vencimiento de certificado'
                          placeholder='dd/mm/aaaa'
                          minDate={new Date(new Date().setDate(new Date().getDate() + 1))}
                          value={dfCertificateDue}
                          onChange={(e) => setDfCertificateDue(e.target.value)}
                          disable={activeStep !== 2 || readOnly}
                        />
                        <TextArea
                          disable={activeStep !== 2 || readOnly}
                          className='w-full'
                          value={afipCertificate}
                          onChange={(e) => setAfipCertificate(e.target.value)}
                        />
                      </div>
                    ),
                  },
                  {
                    title: 'Paso 4 - Conexión con ARCA',
                    icon: 'pi-share-alt',
                    mainBtn: {
                      label: 'Probar',
                      className: 'btn-ghost',
                      onClick: () => {
                        if (readOnly || !healthEntityId || healthEntityId <= 0) return
                        if (!validateAfipBaseData()) return

                        setIsAfipConnectionTestingLoading(true)
                        return testHealthEntityAfipConnection(healthEntityId, healthEntity.afipproductionmode ?? false).then((response) => {
                          setIsAfipConnectionTestingLoading(false)

                          if (!response.success) {
                            setIsAfipConnectionSuccessful(false)
                            dispatch(setNotification({
                              title: response.errormessage,
                              icon: 'pi-times',
                              typeOfNotification: ETypeOfNotification.ERROR,
                            }))
                            return
                          }
                          setIsAfipConnectionSuccessful(true)
                          stepsRef.current?.nextStep()
                          dispatch(setNotification({
                            title: 'La conexión con ARCA ha sido exitosa',
                            icon: 'pi-check',
                            typeOfNotification: ETypeOfNotification.INFO,
                          }))
                        })
                      },
                    },
                    description: 'Probaremos si la conexión con ARCA es válida.',
                    lastStep: true,
                    buttonResult: (
                      <React.Fragment>
                        {isAfipConnectionSuccessful !== null ? <span className='primary ml-3'>Test {isAfipConnectionSuccessful ? 'exitoso' : 'fallido'}</span> : <></>}
                        <i className={classNames('pi primary border-circle shadow-1 p-1 ml-2', {
                          hidden: !isAfipConnectionTestingLoading && isAfipConnectionSuccessful === null,
                          'pi-check': !isAfipConnectionTestingLoading && isAfipConnectionSuccessful,
                          'pi-times': !isAfipConnectionTestingLoading && !isAfipConnectionSuccessful,
                          'pi-spin pi-spinner': isAfipConnectionTestingLoading,
                        })} />
                      </React.Fragment>
                    ),
                  },
                ]}
              />
            </div>
          </fieldset>
        </div>
      </div>

      <ResetAfipConnectionModal
        openResetAfipConnectionModal={openResetAfipConnectionModal}
        setOpenResetAfipConnectionModal={setOpenResetAfipConnectionModal}
        doReset={(doReset: boolean) => {
          if (!doReset) {
            setOpenResetAfipConnectionModal(false)
            return
          }

          setHealthEntity(prev => ({ ...prev, afipalias: provisoryAfipAlias }))
          stepsRef.current?.setActiveStep(0)
          setIsAfipConnectionSuccessful(null)
          setOpenResetAfipConnectionModal(false)
        }}
      />
    </React.Fragment>
  )
}
